import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

function createGradient(color1, color2) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

// SETUP COLORS
const PRIMARY = {
  lighter: '#C8FACD',
  light: '#5BE584',
  main: '#00AB55',
  dark: '#007B55',
  darker: '#005249',
};
const SECONDARY = {
  lighter: '#D6E4FF',
  light: '#84A9FF',
  main: '#3366FF',
  dark: '#1939B7',
  darker: '#091A7A',
};
const INFO = {
  lighter: '#D0F2FF',
  light: '#74CAFF',
  main: '#1890FF',
  dark: '#0C53B7',
  darker: '#04297A',
};
const SUCCESS = {
  lighter: '#E9FCD4',
  light: '#AAF27F',
  main: '#54D62C',
  dark: '#229A16',
  darker: '#08660D',
};
const WARNING = {
  lighter: '#FFF7CD',
  light: '#FFE16A',
  main: '#FFC107',
  dark: '#B78103',
  darker: '#7A4F01',
};
const ERROR = {
  lighter: '#FFE7D9',
  light: '#FFA48D',
  main: '#FF4842',
  dark: '#B72136',
  darker: '#7A0C2E',
};

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  500_8: alpha('#919EAB', 0.08),
  500_12: alpha('#919EAB', 0.12),
  500_16: alpha('#919EAB', 0.16),
  500_24: alpha('#919EAB', 0.24),
  500_32: alpha('#919EAB', 0.32),
  500_48: alpha('#919EAB', 0.48),
  500_56: alpha('#919EAB', 0.56),
  500_80: alpha('#919EAB', 0.8),
};

const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main),
};

const CHART_COLORS = {
  violet: ['#826AF9', '#9E86FF', '#D0AEFF', '#F7D2FF'],
  blue: ['#2D99FF', '#83CFFF', '#A5F3FF', '#CCFAFF'],
  green: ['#2CD9C5', '#60F1C8', '#A4F7CC', '#C0F2DC'],
  yellow: ['#FFE700', '#FFEF5A', '#FFF7AE', '#FFF3D6'],
  red: ['#FF6C40', '#FF8F6D', '#FFBD98', '#FFF2D4'],
};

const COMMON = {
  common: { black: '#000', white: '#fff' },
  primary: { ...PRIMARY, contrastText: '#fff' },
  secondary: { ...SECONDARY, contrastText: '#fff' },
  info: { ...INFO, contrastText: '#fff' },
  success: { ...SUCCESS, contrastText: GREY[800] },
  warning: { ...WARNING, contrastText: GREY[800] },
  error: { ...ERROR, contrastText: '#fff' },
  grey: GREY,
  gradients: GRADIENTS,
  chart: CHART_COLORS,
  divider: GREY[500_24],
  action: {
    hover: GREY[500_8],
    selected: GREY[500_12],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: '#fff',
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

const palette = {
  header: {
    bgColor: '#930000',
    menuBg: '#01a101',
    menu: '#fff',
    deposit_btn: {
      bg: 'linear-gradient(180deg, #FEE504 0%, #ED7E40 100%)',
      color: '#fff',
    },
    login_btn: {
      border: '#f8da4a',
      bg: 'linear-gradient(0deg, rgb(170, 37, 27), rgb(235, 68, 58))',
      color: '#fff',
    },
    register_btn: {
      border: '#f8da4a',
      bg: 'linear-gradient(0deg, rgb(14, 42, 148), rgb(43, 102, 225))',
      color: '#fff',
    },
    balance_button: {
      bg: 'rgba(255,255,255,0.1)',
      color: '#fff',
    },
  },

  navBarVertical: {
    bg: 'linear-gradient(to right, #290303, #5E0202, #2E0303)',
    subBg: 'rgb(45, 3, 3)',
    menu: {
      bgColor: '#01a101',
      color: '#fff',
    },
  },

  bottomMenu: {
    bgColor: '#FFD633',
    color: `blue`,
    depositBg: `linear-gradient(#1C1E22 0px, #1C1E22 100%)`,
    depositBorder: '#0d840d',
  },

  home: {
    announcement: {
      bg: 'linear-gradient(90deg, #EE0000 0%, #790000 100%)',
      color: '#fff',
    },
    tab: {
      borderBottom: '#c7fe27',
      bgActive: '#c7fe27',
      bgInActive: 'transparent',
      textActive: '#009115',
      textInactive: '#c7fe27',
    },
    sort: {
      labelColor: '#c7fe27',
    },
  },

  footer: {
    bgColor: '#2C0202',
    bgColor2: '#2C0202',
    bgColor3: '#2C0202',
  },

  button: {
    bg: 'linear-gradient(180deg, #ED4A33 45.67%, #FB0500 56.25%)',
    shadow: '0px 0px 8px 0px #FFD633',
    border: '1px solid #FFF1B9',
    gameShadow: '0px 0px 5px 0px #FFD633',
  },

  visitor: {
    dialog: {
      title: '#fff',
      loginDirectly: '#000',
      footerText: '#fff',
      hr: `#fff`,
      otherText: '#EB3715',
    },
    icon: 'grey',
    input: {
      border: '1px solid rgb(117, 117, 117)',
      color: '#fff',
      placeholder: '#BDBDBD',
      bg: 'rgb(28, 8, 8)',
    },
    link: '#EB3715',
    errorText: '#FEE504',
    login: {
      buttonBg: 'linear-gradient(0deg, rgba(63,134,237,1) 0%, rgba(9,185,252,1) 100%)',
      color: '#fff',
      border: '#41a4ff',
    },
    register: {
      buttonBg: 'linear-gradient(0deg, rgba(63,134,237,1) 0%, rgba(9,185,252,1) 100%)',
      color: '#fff',
      border: '#41a4ff',
    },
  },

  transactionDialog: {
    bg: '#0d1a8c',
    border: '#c7fe27',
    tab: {
      bg: '#000000',
      active: '#373644',
      inactive: 'transparent',
      activeTxt: '#fff',
      inactiveTxt: '#fff',
    },
    deposit: {
      copy: '#000',
      inputLabel: '#fff',
      smallText: '#fff',
      largeText: '#fff',
      suggestedTopupBg: 'rgb(28, 15, 15)',
      suggestedTopupTxt: '#fff',
      smallPromoBox: {
        bg: 'rgb(65, 32, 32)',
        active: '#FC4A4A',
        color: '#fff',
      },
      bonusBox: {
        bg: 'rgb(28, 15, 15)',
        color: '#fff',
        border: 'rgb(65, 32, 32)',
      },
      select: {
        bg: 'rgb(28, 15, 15)',
        color: '#fff',
        border: 'rgb(65, 32, 32)',
      },
      selectedBankBox: {
        bg: 'rgb(28, 15, 15)',
        border: 'rgb(65, 32, 32)',
      },
      depositInputBox: {
        bg: 'rgb(28, 15, 15)',
        border: 'rgb(65, 32, 32)',
      },
      depositInput: {
        color: '#fff',
      },
      bankRefNumber: {
        color: '#000',
        border: '#fff4ba',
        outlineBg: '#ffde4a',
        placeholder: '#000',
      },
      confirmBtn: 'linear-gradient(165deg, rgb(255, 94, 94) -17.16%, rgb(139, 0, 0) 91.36%)',
      bepositBtnText: '#fff',
      depositAmt: '#fff',
    },
    withdraw: {
      text: '#fff',
      suggestedWthdrwBg: '#b19100',
      availableBalanceTxt: 'rgba(255, 255, 255, 0.85)',
      withdrawInputBox: {
        bg: 'rgb(28, 15, 15)',
        border: 'rgb(65, 32, 32)',
        shadow: 'rgba(220, 60, 60, 0.2)',
      },
      noBankAccount: '#fff',
      withdrawText: '#fff',
      confirmBtnBg: 'linear-gradient(180deg, #FEE504 0%, #ED7E40 100%)',
    },
  },

  light: {
    ...COMMON,
    mode: 'light',
    text: { primary: GREY[800], secondary: GREY[600], disabled: GREY[500] },
    background: { paper: '#fff', default: '#fff', neutral: GREY[200] },
    action: { active: GREY[600], ...COMMON.action },
  },
  dark: {
    ...COMMON,
    mode: 'dark',
    text: { primary: '#fff', secondary: GREY[500], disabled: GREY[600] },
    background: { paper: '#000', default: '#000', neutral: GREY[500_16], dark: '#1D1C21' },
    action: { active: GREY[500], ...COMMON.action },
  },
};

export default palette;
